import type { FC } from 'react';
import React, { useState, useRef, useLayoutEffect } from 'react';
import { FormattedMessage } from 'react-intl-next';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkButton from '@atlaskit/button/custom-theme-button';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';

import { fg } from '@confluence/feature-gating';
import { EXTENSION_POINT_SPACE_SETTINGS_TABS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import type { ForgeUISpaceSettingsExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import { ForgeKeyboardShortcut } from '@confluence/forge-ui/entry-points/ForgeKeyboardShortcut.ts';
import { isBlockedByAppAccessRule } from '@confluence/forge-ui/entry-points/appAccessRule.ts';
import { getAppId } from '@confluence/forge-ui/entry-points/appData.ts';
import { SPACE_SETTINGS_FORGE_APPS } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';

import { isNonNullable } from '../utils.ts';
import { isForgeAppMatchingRoute } from '../helpers/webItem.ts';

import { WebItemTabs } from './WebItemTabs.tsx';
import { ForgeAppTabs } from './ForgeAppTabs.tsx';
// eslint-disable-next-line import/no-cycle
import type { SpaceSettingsNavigationProps } from './SpaceSettingsNavigation.tsx';
import { ANALYTICS_SOURCE } from './SpaceSettingsNavigation.tsx';
import { ForgeAppsLoader } from './ForgeAppsLoader.tsx';
import type { SpaceSettingsNavigationQuery_webItemSections_WebSection_items_WebItem as SpaceSettingsNavigationQuery_webItemSections_items } from './__types__/SpaceSettingsNavigationQuery.ts';

const APPROX_OVERFLOW_BUTTON_WIDTH = 75;

export const customTheme = (isSelected, currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			width: 'auto',
			textDecoration: 'none',
			marginTop: token('space.075'),
			color: isSelected ? token('color.text.selected') : token('color.text'),
			background: 'none',
			'&:hover': {
				textDecoration: 'none',
				color: token('color.text.selected'),
			},
		},
		// required by TS
		spinnerStyles,
	};
};

function howManyTabsFit(individualTabWidths: number[], availableWidth: number) {
	let numberTabs = 0,
		totalWidthTabs = 0;
	for (let i = 0; i < individualTabWidths.length; i++) {
		// Report back how much width we
		totalWidthTabs += individualTabWidths[i];
		if (totalWidthTabs > availableWidth) {
			break;
		}
		numberTabs++;
	}
	return { numberTabs, totalWidthTabs };
}

type TabsControllerProps = SpaceSettingsNavigationProps & {
	width: number;
	webItems: SpaceSettingsNavigationQuery_webItemSections_items[];
	innerNavigationWebItem?: SpaceSettingsNavigationQuery_webItemSections_items | null;
	showForgeAppTabs: boolean;
};

export const TabsController: FC<TabsControllerProps> = ({
	width,
	webItems,
	innerNavigationWebItem,
	forgeAppId,
	forgeEnvId,
	forgeRoutePath,
	onLoadForgeError,
	showForgeAppTabs,
	onLoadForgeApps,
	spaceKey,
}) => {
	// Forge apps will be loaded by a child component and set into state locally.
	const [forgeApps, setForgeApps] = useState([]);

	// Store how many tabs we can show on screen, based on their widths + the available nav width.
	// webItems and Forge apps load asynchronously, so we store and calculate their widths separately.
	const [maxWebItemTabs, setMaxWebItemTabs] = useState(webItems.length);
	const [maxForgeTabs, setMaxForgeTabs] = useState<number | null>(null);

	// Once tabs have rendered, we store each one's calculated width into an ordered array here
	// for comparison against the available nav width. We don't want this to trigger a rerender.
	const webItemTabWidths = useRef<number[]>([]).current;
	const forgeTabWidths = useRef<number[]>([]).current;

	const { push } = useRouteActions();

	useLayoutEffect(() => {
		// Account for some extra room needed on the nav for the overflow button
		const availableWidth = Math.max(width - APPROX_OVERFLOW_BUTTON_WIDTH, 0);
		if (
			availableWidth &&
			webItems.length === webItemTabWidths.length &&
			forgeApps.length === forgeTabWidths.length
		) {
			const { numberTabs: allowedWebItemTabs, totalWidthTabs } = howManyTabsFit(
				webItemTabWidths,
				availableWidth,
			);
			setMaxWebItemTabs(allowedWebItemTabs);

			// Only calculate and set width of Forge tabs once we're sure Forge apps have loaded - this happens
			// asynchronously from the webItems above
			if (forgeApps.length) {
				const { numberTabs: allowedForgeAppTabs } = howManyTabsFit(
					// webItem tabs appear first, decreasing the available width for Forge app tabs
					forgeTabWidths,
					availableWidth - totalWidthTabs,
				);
				setMaxForgeTabs(allowedForgeAppTabs);
			}
		}
	}, [
		// Run this update when width changes (e.g. due to window resize), or when webItems or forgeApps change/load
		webItemTabWidths,
		webItems.length,
		width,
		forgeApps,
		forgeTabWidths,
	]);

	// In order for us to calculate how many tabs can be shown on screen, we must first know the length of
	// each tab - this is dynamic as tab number and length depends on our providers (webItems, Forge) and
	// includes configurable plugins. There is no max width set on individual tabs.
	// To know the length of each tab, we must render each tab on screen, at which point the setWidth callback
	// will be invoked, and we will know exactly how much space we have for each tab whenever the window is resized.
	// So the first render for webItemTabs and forgeAppTabs respectively MUST include ALL tabs in each set.
	// Then, we will calculate the available width and update state, triggering a rerender that pushes any
	// "leftover" tabs from each set into the overflow menu.
	// On first render, maxWebItemTabs == webItems.length so we will show all of them. maxWebItemTabs will then
	// be calculated and reset in useLayoutEffect.
	const webItemTabsToShow = webItems.slice(0, maxWebItemTabs);
	const leftoverWebItemTabs = webItems.slice(maxWebItemTabs);
	// Forge app tabs load separately from and more slowly than webItem tabs above, so we store their max #
	// separately. Even if all webItem tabs have loaded and some are pushed to the overflow menu, we must display
	// every Forge app tab during the first render where we have Forge app data (maxForgeTabs == null), so that we
	// can gather the individual width of each tab via setWidth. Then when useLayoutEffect runs we calculate the
	// actual available width, set maxForgeTabs == some number, and this will trigger a rerender upon which the
	// right number of Forge tabs will be pushed to overflow menu here.
	// Forge tabs always appear *after* webItem tabs.
	const forgeNumAllowedTabs = maxForgeTabs === null ? forgeApps.length : maxForgeTabs;
	const forgeTabsToShow = forgeApps.slice(0, forgeNumAllowedTabs);
	const leftoverForgeItemTabs = forgeApps.slice(forgeNumAllowedTabs);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleSetWebItemTabWidth = (tabWidth, index) => {
		webItemTabWidths[index] = tabWidth;
	};

	const handleSetForgeTabWidth = (tabWidth, index) => {
		forgeTabWidths[index] = tabWidth;
	};

	const handleLoadForgeApps = (apps) => {
		// Apps blocked by App Access Rule should be hidden in tabs. As a result we filter
		// out all blocked apps before performing any display calculations.
		const appsAllowedByAppAccessRule = apps.filter((app) => !isBlockedByAppAccessRule(app));

		setForgeApps(appsAllowedByAppAccessRule);
		onLoadForgeApps && onLoadForgeApps(appsAllowedByAppAccessRule);
	};

	const createClickEvent = (clickedTabKey?: string | null, source?: string) => () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'spaceSettingsInnerNavLink',
				source: source || ANALYTICS_SOURCE,
				attributes: {
					linkId: clickedTabKey,
				},
			},
		}).fire();
	};

	const isDropdownButtonSelected = Boolean(
		(innerNavigationWebItem?.completeKey &&
			leftoverWebItemTabs.some(
				(item) => item.completeKey === innerNavigationWebItem.completeKey,
			)) ||
		leftoverForgeItemTabs.some((app: ForgeUISpaceSettingsExtensionType) =>
			isForgeAppMatchingRoute(app, forgeAppId, forgeEnvId, forgeRoutePath),
		),
	);

	const forgeKeyboardShortcuts = forgeApps.map((f: ForgeUISpaceSettingsExtensionType) => (
		<ForgeKeyboardShortcut
			key={f.id}
			module={f}
			action={() =>
				push(
					SPACE_SETTINGS_FORGE_APPS.toUrl({
						spaceKey,
						appId: getAppId(f),
						envId: f.environmentId,
						forgeManifestRoute: f.properties?.route,
					}),
				)
			}
		/>
	));

	return (
		<>
			<ExperienceStart name={EXTENSION_POINT_SPACE_SETTINGS_TABS_EXPERIENCE} />
			{forgeKeyboardShortcuts}
			<WebItemTabs
				webItems={webItemTabsToShow}
				createClickEvent={createClickEvent}
				innerNavigationWebItem={innerNavigationWebItem}
				isDropdown={false}
				setWidth={handleSetWebItemTabWidth}
			/>
			<ForgeAppTabs
				apps={forgeTabsToShow.filter(isNonNullable)}
				createClickEvent={createClickEvent}
				forgeAppId={forgeAppId}
				forgeEnvId={forgeEnvId}
				forgeRoutePath={forgeRoutePath}
				spaceKey={spaceKey}
				isDropdown={false}
				setWidth={handleSetForgeTabWidth}
			/>
			{leftoverWebItemTabs.length || leftoverForgeItemTabs.length ? (
				<DropdownMenu
					trigger={({ triggerRef, ...props }) => (
						<AkButton
							{...props}
							theme={(currentTheme, themeProps) =>
								customTheme(isDropdownButtonSelected, currentTheme, themeProps)
							}
							spacing="compact"
							iconAfter={<ChevronDownIcon label="" size="small" />}
							shouldFitContainer
							ref={triggerRef}
							testId="space-settings-tabs-more"
						>
							<FormattedMessage
								id="space-settings.navigation.tabs.overflow-button"
								defaultMessage="More"
								description="Text for a button which opens a dropdown menu when clicked, revealing the overflowed tabs"
							/>
						</AkButton>
					)}
					shouldFlip={false}
					placement="bottom-end"
					shouldRenderToParent={fg('should-render-to-parent-should-be-trueadmin-experi')}
				>
					<DropdownItemGroup testId="space-settings-tabs-more-items">
						<WebItemTabs
							webItems={leftoverWebItemTabs}
							createClickEvent={createClickEvent}
							innerNavigationWebItem={innerNavigationWebItem}
							isDropdown
						/>
						<ForgeAppTabs
							apps={leftoverForgeItemTabs}
							createClickEvent={createClickEvent}
							forgeAppId={forgeAppId}
							forgeEnvId={forgeEnvId}
							forgeRoutePath={forgeRoutePath}
							spaceKey={spaceKey}
							isDropdown
						/>
					</DropdownItemGroup>
				</DropdownMenu>
			) : null}
			{showForgeAppTabs ? (
				// this is the component that actually handles loading of Forge apps,
				// but doesn't render them
				<ForgeAppsLoader onLoadForgeApps={handleLoadForgeApps} onError={onLoadForgeError} />
			) : null}
			<ExperienceSuccess name={EXTENSION_POINT_SPACE_SETTINGS_TABS_EXPERIENCE} />
		</>
	);
};
